import axiosInstance from "@/utils/helper/axiosInstance";

const CORE_BASE = "tenant-portal/core";

export const orgService = Object.freeze({
  getBootstrapData: async () => {
    const res = await axiosInstance.get(`${CORE_BASE}/bootstrap`);
    return res?.data;
  },

  /**
   * Calls /core/bootstrap and reshapes the response to match
   * the shape that SettingProvider expects from /user/setting.
   * This allows portal-off tenants to boot without hitting 403.
   */
  getBootstrapForSettings: async () => {
    const res = await axiosInstance.get(`${CORE_BASE}/bootstrap`);
    const raw = res?.data;
    const bootstrapData = raw?.data;

    // Reshape to match /user/setting response: { success, data: { tenant, settings } }
    // Bootstrap returns settings nested as: data.settings = { tenant: {...}, settings: [...] }
    const settingsPayload = bootstrapData?.settings;
    return {
      success: raw?.success,
      data: {
        tenant: settingsPayload?.tenant || bootstrapData?.tenant,
        settings: settingsPayload?.settings || null,
      },
      // Extra fields not in /user/setting but needed by Login redirect
      admin_portal_enabled: bootstrapData?.admin_portal_enabled ?? true,
      user: bootstrapData?.user,
    };
  },

  getSeatUsage: async () => {
    const res = await axiosInstance.get(`${CORE_BASE}/org/seats`);
    return res?.data;
  },

  getUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    seat_type?: string;
    status?: string;
    sort_by?: string;
    sort_order?: string;
  }) => {
    const res = await axiosInstance.get(`${CORE_BASE}/org/users`, { params });
    return res?.data;
  },

  addUser: async (data: {
    name: string;
    email: string;
    seat_type: string;
    roles: string[];
    phone?: string;
  }) => {
    const res = await axiosInstance.post(`${CORE_BASE}/org/users`, data);
    return res?.data;
  },

  updateUser: async (id: number, data: {
    name?: string;
    seat_type?: string;
    roles?: string[];
    phone?: string;
  }) => {
    const res = await axiosInstance.patch(`${CORE_BASE}/org/users/${id}`, data);
    return res?.data;
  },

  deactivateUser: async (id: number) => {
    const res = await axiosInstance.delete(`${CORE_BASE}/org/users/${id}`);
    return res?.data;
  },

  reactivateUser: async (id: number) => {
    const res = await axiosInstance.post(`${CORE_BASE}/org/users/${id}/reactivate`);
    return res?.data;
  },

  createSeatRequest: async (data: {
    seat_type: string;
    quantity: number;
    billing_preference: string;
    acknowledged: boolean;
    notes?: string;
    notify_emails?: string[];
    rate_per_seat?: number;
    prorated_cost?: number;
    remaining_term_months?: number;
  }) => {
    const res = await axiosInstance.post(`${CORE_BASE}/org/seat-requests`, data);
    return res?.data;
  },

  getSeatRequests: async (params?: { page?: number; limit?: number }) => {
    const res = await axiosInstance.get(`${CORE_BASE}/org/seat-requests`, { params });
    return res?.data;
  },
});
